import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import useIsomorphicLayoutEffect from '~/common/hooks/useIsomorphicLayoutEffect';
import styled from 'styled-components';
import gsap from 'gsap';
import { CookieService } from '~/common/utils/cookies';
import { COOKIE_NAMES } from '~/common/constants/localStorage';
import { CUSTOM_EVENTS } from '~/common/constants/events';
import { hasUserSubscribedToNewsletter } from '~/common/utils/newsletterFlag';
import { fontFamilies } from '~/theme/typography';
import { useNavigation } from '~/common/hooks/useNavigation';
import { useNewsletterSubscribe } from '~/common/hooks/useNewsletterSubscribe';
import { HoneypotField } from '~/components/HoneypotField';
import CloseModal from '~/components/icons/CloseModal';
import ArrowSubmit from '~/components/icons/ArrowSubmit';
import { regionModalOpenState } from '~/recoil/useRegion';

const OPEN_DURATION = 0.6;
const CLOSE_DURATION = 0.4;
const OPEN_DELAY_MS = 3000;
const AUTO_CLOSE_DELAY_MS = 2000;
const ONE_WEEK_SECONDS = 7 * 24 * 60 * 60;

export const SignupModal = () => {
  const { goToPrivacy } = useNavigation();
  const isRegionModalOpen = useRecoilValue(regionModalOpenState);
  const [isOpen, setIsOpen] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    email,
    setEmail,
    subscribed,
    submitError,
    errorMessage,
    handleSubmit,
    hasValue,
  } = useNewsletterSubscribe();

  useEffect(() => {
    if (isRegionModalOpen) return;

    const scheduleShow = () => {
      if (hasUserSubscribedToNewsletter()) return;
      if (CookieService.get(COOKIE_NAMES.signupModalDismissed)) return;

      showTimerRef.current = setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);
    };

    if (CookieService.hasMarketingConsent()) {
      scheduleShow();

      return () => {
        if (showTimerRef.current) clearTimeout(showTimerRef.current);
      };
    }

    const handleConsentUpdated = () => {
      if (CookieService.hasMarketingConsent()) scheduleShow();
    };

    window.addEventListener(
      CUSTOM_EVENTS.cookieConsentUpdated,
      handleConsentUpdated,
    );

    return () => {
      window.removeEventListener(
        CUSTOM_EVENTS.cookieConsentUpdated,
        handleConsentUpdated,
      );

      if (showTimerRef.current) clearTimeout(showTimerRef.current);
    };
  }, [isRegionModalOpen]);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const ctx = gsap.context(() => {
      gsap.to(modalRef.current, {
        opacity: 1,
        scale: 1,
        duration: OPEN_DURATION,
        ease: 'power4.out',
      });
    });

    return () => ctx.revert();
  }, [isOpen]);

  useIsomorphicLayoutEffect(() => {
    if (!isOpen) {
      setContentHeight(null);
      return;
    }

    if (subscribed) return;

    const el = formSectionRef.current;
    if (!el) return;

    const measure = () => {
      const height = el.offsetHeight;
      if (height > 0) setContentHeight(height);
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => ro.disconnect();
  }, [isOpen, subscribed]);

  useEffect(() => {
    if (!subscribed || !modalRef.current) return;

    const timer = setTimeout(() => {
      if (!modalRef.current) return;

      gsap.to(modalRef.current, {
        opacity: 0,
        scale: 0.96,
        duration: CLOSE_DURATION,
        ease: 'power4.in',
        onComplete: () => setIsOpen(false),
      });
    }, AUTO_CLOSE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [subscribed]);

  const handleCloseClick = () => {
    if (!modalRef.current) return;

    const wasSubscribed = subscribed;
    gsap.to(modalRef.current, {
      opacity: 0,
      scale: 0.96,
      duration: CLOSE_DURATION,
      ease: 'power4.in',
      onComplete: () => {
        setIsOpen(false);

        if (!wasSubscribed && CookieService.hasFunctionalityConsent()) {
          CookieService.set(COOKIE_NAMES.signupModalDismissed, '1', {
            maxAge: ONE_WEEK_SECONDS,
          });
        }
      },
    });
  };

  if (!isOpen) return null;

  return (
    <Wrapper>
      <Modal
        ref={modalRef}
        role="dialog"
        aria-labelledby="signup-modal-title"
        aria-modal="true"
      >
        {!subscribed && (
          <CloseButton
            type="button"
            onClick={handleCloseClick}
            aria-label="Close signup modal"
          >
            <CloseModal />
          </CloseButton>
        )}

        <ContentWrapper $contentHeight={contentHeight}>
          <FormSection
            ref={formSectionRef}
            $visible={!subscribed}
            aria-hidden={subscribed}
          >
            <Title id="signup-modal-title">SIGN UP FOR UPDATES</Title>
            <Subtitle>BE THE FIRST TO KNOW</Subtitle>

            <Form onSubmit={handleSubmit}>
              <HoneypotField />
              <InputRow>
                <UnderlineInput
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ENTER YOUR EMAIL"
                  aria-label="Email address for newsletter signup"
                  $submitError={submitError}
                />
                <SubmitButton
                  type="submit"
                  disabled={!hasValue || subscribed}
                  aria-label="Subscribe"
                >
                  <ArrowSubmitIconWrapper $disabled={!hasValue || subscribed} />
                </SubmitButton>
              </InputRow>
              {errorMessage && (
                <ErrorMessage role="alert">{errorMessage}</ErrorMessage>
              )}
            </Form>

            <Disclaimer>
              BY SUBSCRIBING, YOU AGREE TO RECEIVE EMAILS FROM BROKEN PLANET.
              YOU CAN UNSUBSCRIBE AT ANY TIME. SEE OUR{' '}
              <PrivacyLink type="button" onClick={goToPrivacy}>
                PRIVACY POLICY
              </PrivacyLink>{' '}
              FOR DETAILS.
            </Disclaimer>
          </FormSection>

          <ThankYouSection $visible={subscribed} aria-hidden={!subscribed}>
            <Title>SIGN UP FOR UPDATES</Title>
            <ThankYouText>Thank you for subscribing</ThankYouText>
          </ThankYouSection>
        </ContentWrapper>
      </Modal>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.signupModal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  pointer-events: none;
`;

const Modal = styled.div`
  pointer-events: auto;
  width: 100%;
  max-width: 600px;
  background: ${({ theme }) => theme.colors.modalBackgroundBlur};
  backdrop-filter: blur(4px);
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.modal};
  padding: 40px;
  position: relative;
  opacity: 0;
  transform: scale(0.96);
  will-change: opacity, transform;

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 20px 16px;
  }
`;

const ContentWrapper = styled.div<{ $contentHeight: number | null }>`
  position: relative;
  height: ${({ $contentHeight }) =>
    $contentHeight != null ? `${$contentHeight}px` : 'auto'};
`;

const FormSection = styled.div<{ $visible: boolean }>`
  position: ${({ $visible }) => ($visible ? 'relative' : 'absolute')};
  ${({ $visible }) => !$visible && 'inset: 0;'}
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? 0 : '-8px')});
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
`;

const ThankYouSection = styled.div<{ $visible: boolean }>`
  position: ${({ $visible }) => ($visible ? 'relative' : 'absolute')};
  ${({ $visible }) => !$visible && 'inset: 0;'}
  ${({ $visible }) => $visible && 'min-height: 100%;'}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? 0 : '8px')});
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    opacity: 0.85;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    top: 8px;
    right: 8px;
  }
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 40px;
  font-style: normal;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin: 0 0 8px 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  font-family: ${fontFamilies.CircularStd};
  font-size: 24px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin: 0 0 16px 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 16px;
  }
`;

const Form = styled.form`
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 52px;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  background: transparent;
  box-sizing: border-box;
`;

const UnderlineInput = styled.input<{ $submitError: boolean }>`
  flex: 1;
  align-self: stretch;
  min-width: 0;
  background: transparent;
  -webkit-user-select: text;
  border: none;
  outline: none;
  box-shadow: none;
  padding: 0;
  font-family: ${fontFamilies.CircularStd};
  font-size: 16px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme, $submitError }) =>
    $submitError ? theme.colors.error : theme.colors.white};

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    border: none;
    box-shadow: none;
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.greyDisabled2};
    font-family: ${fontFamilies.CircularStd};
    font-size: 16px;
    font-style: normal;
    font-weight: 450;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
`;

const SubmitButton = styled.button`
  flex-shrink: 0;
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ArrowSubmitIconWrapper = styled(ArrowSubmit)<{ $disabled: boolean }>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.greyDisabled2 : theme.colors.white};
`;

const ThankYouText = styled.p`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 1.5rem;
  line-height: normal;
  letter-spacing: 0.03125rem;
  text-transform: uppercase;
  text-align: center;
  margin: 0;

  @media ${({ theme }) => theme.breakpoints.mt} {
    font-size: 1rem;
  }
`;

const ErrorMessage = styled.p`
  font-size: 0.875rem;
  line-height: normal;
  color: ${({ theme }) => theme.colors.error};
  font-family: ${fontFamilies.CircularStd};
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.03125rem;
  margin: 0;
`;

const Disclaimer = styled.p`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  font-family: ${fontFamilies.CircularStd};
  font-size: 14px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 0.07px;
  text-transform: uppercase;
  margin: 0;
`;

const PrivacyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: inline;
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;
